import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  convertDateStringsToDates,
  convertKeysToCamelCase,
} from "@/services/adapters/caseConversion/caseConversions"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import type { DraftArticle } from "../components/supplier-declarations/inquiry/draft/AddArticleDialog"
import {
  type AttachmentFormat,
  type InquiryDraftItem,
  type InquiryEmailCopy,
  type Lang,
  type Stage,
} from "../components/supplier-declarations/inquiry/draft/inquiryDraftMock"
import { getApiErrorDetail, toastApiError } from "./apiError"
import type { NoteOut } from "./notesApi"
import type { InquirySettingsStatus } from "./supplierInquiriesApi"
import { templateSettingsQuery } from "./templateSettingsApi"

const BASE = "/material-compliance/supplier-inquiries"

// True for an axios 404 — used to retry a read that races a just-created row (replica lag).
function isNotFoundError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { response?: { status?: number } }).response?.status === 404
  )
}

// ─── Backend response shape (apiClientV2 converts snake_case → camelCase) ───────

interface InquiryArticleOut {
  articleId: string
  articleNumber: string | null
  description: string | null
  supplierArticleNumber: string | null
  productGroup?: string | null
}

export interface SupplierInquiryDetail {
  id: string
  reference: string | null
  supplierId: string | null
  supplierName: string | null
  status: string
  frameworks: string[]
  emailTo?: string | null
  recipientsTo: string[]
  recipientsCc: string[]
  emailCopy?: InquiryEmailCopy
  emailSubject?: string | null
  emailBody?: string | null
  reminderEmailSubject?: string | null
  reminderEmailBody?: string | null
  language: string | null
  attachmentFormat?: string | null
  // Settings > Regulations: what the format's documents ask about (null = everything
  // the documents carry), and NO_INQUIRY_REGULATIONS when that format covers none of
  // the org's selected regulations (the send would be refused).
  askedRegulations?: string[] | null
  // The regulations the inquiry is meant to ask (its pick, else Settings > Regelwerke);
  // keys missing from askedRegulations are ones this format has no section for.
  regulationKeys?: string[] | null
  regulationScopeError?: string | null
  /** "explicit" once someone picked the regulations; "default" follows Settings. */
  scopeSource?: "default" | "explicit" | null
  articles: InquiryArticleOut[]
  dueAt: string | null
  // Backend-rendered questionnaire HTML: { first: {en,de}, reminder: {en,de} }.
  questionnaire: Record<Stage, Record<Lang, string>>
  // configured | unconfigured | unavailable — whether the org's template settings
  // resolved for this read (unavailable = a genuine, retryable read failure).
  settingsStatus?: InquirySettingsStatus
}

// ─── Mapping BE detail → the InquiryDraftItem contract the composer consumes ────

function toDraftArticle(a: InquiryArticleOut): DraftArticle {
  return {
    id: a.articleId,
    articleNumber: a.articleNumber ?? "",
    name: a.description ?? "",
    supplierArticleNumber: a.supplierArticleNumber ?? undefined,
    productGroup: a.productGroup ?? undefined,
  }
}

function normalizeEmailCopy(d: SupplierInquiryDetail): InquiryEmailCopy {
  return {
    first: {
      en: d.emailCopy?.first?.en ?? {
        subject: d.emailSubject ?? "",
        body: d.emailBody ?? "",
      },
      de: d.emailCopy?.first?.de ?? {
        subject: d.emailSubject ?? "",
        body: d.emailBody ?? "",
      },
    },
    reminder: {
      en: d.emailCopy?.reminder?.en ?? {
        subject: d.reminderEmailSubject ?? "",
        body: d.reminderEmailBody ?? "",
      },
      de: d.emailCopy?.reminder?.de ?? {
        subject: d.reminderEmailSubject ?? "",
        body: d.reminderEmailBody ?? "",
      },
    },
  }
}

export function toDraftItem(d: SupplierInquiryDetail): InquiryDraftItem {
  return {
    id: d.id,
    reference: d.reference ?? null,
    status: d.status,
    supplierId: d.supplierId ?? "",
    // The inquiry detail payload has no supplier_number today; don't alias the name
    // into it (would show the name where a number is expected).
    supplierNumber: "",
    supplierName: d.supplierName ?? "",
    // recipients list, falling back to the single email_to (matches the send path).
    contactEmails: d.recipientsTo?.length
      ? d.recipientsTo
      : d.emailTo
        ? [d.emailTo]
        : [],
    contactEmailsCc: d.recipientsCc ?? [],
    frameworks: d.frameworks ?? [],
    articlesCount: d.articles?.length ?? 0,
    articles: (d.articles ?? []).map(toDraftArticle),
    questionnaire: d.questionnaire,
    emailCopy: normalizeEmailCopy(d),
    // null (not "de") when unset, so the draft page falls back to the UI locale.
    language: d.language === "en" || d.language === "de" ? d.language : null,
    // "none" (the declaration-review Rückfrage's no-attachment format, stored by
    // create_declaration_inquiry and kept standing on reminders) is passed through
    // as-is: the ordinary composer never SETS it (the backend refuses that write),
    // but reading one back must not silently promote it to "pdf". Any other unknown
    // value still falls back to "pdf".
    attachmentFormat:
      d.attachmentFormat === "excel" ||
      d.attachmentFormat === "pdf_and_excel" ||
      d.attachmentFormat === "none"
        ? d.attachmentFormat
        : "pdf",
    dueAt: d.dueAt,
    settingsStatus: d.settingsStatus ?? "configured",
    askedRegulations: d.askedRegulations ?? null,
    regulationKeys: d.regulationKeys ?? null,
    regulationScopeError: d.regulationScopeError ?? null,
    scopeSource: d.scopeSource ?? null,
  }
}

// ─── Create drafts (POST) — called once, from the supplier picker ───────────────
// Returns the created inquiry ids so the caller navigates to the draft page by
// INQUIRY id (not supplier id). Creating must NOT happen on the draft page load,
// or every reload would spawn a fresh draft and lose edits.
export interface InquiryRecipientIn {
  supplierId: string
  /** Omit to default to the supplier's own linked articles; [] for an explicit
   * empty scope; a list to scope the questionnaire to exactly those articles. */
  articleIds?: string[]
}

export interface CreateInquiriesParams {
  supplierIds?: string[]
  // Per-supplier article scope for one batch create — takes priority over
  // `supplierIds` (defines the supplier set too). Use when different
  // suppliers in the same send need different article lists.
  recipients?: InquiryRecipientIn[]
  frameworks?: string[]
  /** The regulations every draft asks, as keys (TUR-1730). Omit to follow the
   *  org's Settings › Regelwerke live; send only when the user changed the ticks. */
  regulationKeys?: string[]
  articleIds?: string[]
  /** Draft language. Omit for the org's document language, see
   *  `newInquiryLanguage`. */
  language?: Lang
  /** Why the inquiry is raised — the backend's `InquiryTrigger` vocabulary.
   *  Studio V2's Anfordern passes "declaration_review" together with the
   *  `source*` fields below, the same shape the statement-level Rückfrage
   *  endpoint writes; every other caller keeps the "manual" default. */
  triggerType?: "manual" | "declaration_review"
  triggerDetail?: string
  sourceDeclarationId?: string
  sourceFramework?: string
  sourceStatementId?: string
}

/**
 * POST the batch create. One draft per resolved supplier, nothing sent.
 *
 * `scope` is only ever "single" or "multi" here. The backend also accepts
 * "tag", but no screen uses it, so neither does this — an agent-only selection
 * path would leave no UI able to reproduce it.
 */
export async function createInquiriesRequest(
  params: CreateInquiriesParams,
  fallbackLanguage: Lang,
): Promise<SupplierInquiryDetail[]> {
  const hasRecipients = !!params.recipients?.length
  const { data } = await apiClientV2.post<SupplierInquiryDetail[]>(BASE, {
    scope:
      !hasRecipients && params.supplierIds?.length === 1 ? "single" : "multi",
    ...(hasRecipients
      ? { recipients: params.recipients }
      : { supplierIds: params.supplierIds ?? [] }),
    frameworks: params.frameworks ?? [],
    ...(params.regulationKeys ? { regulationKeys: params.regulationKeys } : {}),
    // `?.length`, not a truthiness check: `[]` is truthy, and the backend reads an
    // empty list as an EXPLICIT empty scope ("honour it, seed nothing") rather than
    // "default to the supplier's own articles" — which ships a questionnaire with no
    // articles on it. Narrowing scope after the fact is the update endpoint's job.
    ...(params.articleIds?.length ? { articleIds: params.articleIds } : {}),
    triggerType: params.triggerType ?? "manual",
    ...(params.triggerDetail ? { triggerDetail: params.triggerDetail } : {}),
    ...(params.sourceDeclarationId
      ? { sourceDeclarationId: params.sourceDeclarationId }
      : {}),
    ...(params.sourceFramework
      ? { sourceFramework: params.sourceFramework }
      : {}),
    ...(params.sourceStatementId
      ? { sourceStatementId: params.sourceStatementId }
      : {}),
    language: params.language ?? fallbackLanguage,
  })
  return data
}

/**
 * Seed the draft caches from the create response so the composer renders
 * immediately and never depends on read-your-writes — a just-created row can
 * 404 on GET under replica lag. Keys must match
 * useInquiryDraft/useInquiryDetail. Any caller that creates drafts and then
 * navigates to the composer MUST call this, or the composer can land on a 404.
 */
export function seedInquiryDraftCaches(
  qc: QueryClient,
  data: SupplierInquiryDetail[],
): string[] {
  const ids = [...data.map((d) => d.id)].sort()
  const byId = new Map(data.map((d) => [d.id, d]))
  const sortedRaw = ids.map((id) => byId.get(id)!)
  qc.setQueryData<{ reference: string; inquiries: InquiryDraftItem[] }>(
    ["mc", "inquiries", "draft", "load", ids],
    {
      reference: sortedRaw[0]?.reference ?? "—",
      inquiries: sortedRaw.map(toDraftItem),
    },
  )
  data.forEach((d) =>
    qc.setQueryData<InquiryDraftItem>(
      ["mc", "inquiries", "detail", d.id],
      toDraftItem(d),
    ),
  )
  return data.map((d) => d.id)
}

/**
 * The language a new inquiry starts in: the one the caller names, else the
 * org's document language (Settings > General), switchable on each draft.
 * The settings are read only when none is named, so a create that names one
 * never waits on them. A read that fails never blocks the create: it falls
 * back to the UI language, as before the org's language was read here.
 */
export async function newInquiryLanguage(
  qc: QueryClient,
  uiLanguage: Lang,
  requested?: Lang,
): Promise<Lang> {
  if (requested) return requested
  try {
    const { defaultLanguage } = await qc.ensureQueryData(templateSettingsQuery)
    return defaultLanguage === "de" || defaultLanguage === "en"
      ? defaultLanguage
      : uiLanguage
  } catch {
    return uiLanguage
  }
}

export function useCreateInquiries() {
  const { currentLanguage, translate } = useTranslation()
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (params: CreateInquiriesParams) => {
      // The language named, else the org's; the UI's only when that can't be
      // read. Templates exist in EN/DE only: DE for a DE UI, else EN (incl.
      // JA). Still switchable via the DE/EN toggle.
      const data = await createInquiriesRequest(
        params,
        await newInquiryLanguage(
          qc,
          currentLanguage === "DE" ? "de" : "en",
          params.language,
        ),
      )
      return seedInquiryDraftCaches(qc, data)
    },
    onError: (error) => {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

// ─── Draft load (GET each inquiry by id) ────────────────────────────────────────
// The draft page receives ?ids=<inquiryId>... and loads each by GET. Reload
// re-fetches the SAME inquiries, so deletions/edits persist (no re-creation).
export function useInquiryDraft(inquiryIds: string[]) {
  // Sort once and use the SAME order for both the cache key and the fetch, so
  // [a,b] and [b,a] share a cache entry AND return the same ordering (primary =
  // inquiries[0] stays stable).
  const ids = [...inquiryIds].sort()
  return useQuery({
    queryKey: ["mc", "inquiries", "draft", "load", ids],
    queryFn: async () => {
      const details = await Promise.all(
        ids.map((id) =>
          apiClientV2
            .get<SupplierInquiryDetail>(`${BASE}/${id}`)
            .then((r) => r.data),
        ),
      )
      const inquiries = details.map(toDraftItem)
      // No shared reference for a bulk draft yet; references are per-inquiry and
      // assigned on send. Show the single inquiry's reference, else an em dash.
      const reference = details[0]?.reference ?? "—"
      return { reference, inquiries }
    },
    enabled: inquiryIds.length > 0,
    // A just-created row can 404 briefly under DB replica lag — retry 404s a few times
    // (the create response already seeds this cache, so this only covers a background refetch).
    retry: (failureCount, error) => failureCount < 5 && isNotFoundError(error),
    retryDelay: (n) => Math.min(400 * 2 ** n, 3000),
  })
}

// ─── Single draft detail (GET /{id}) ────────────────────────────────────────────

export function useInquiryDetail(inquiryId: string | null) {
  return useQuery({
    queryKey: ["mc", "inquiries", "detail", inquiryId],
    queryFn: () =>
      apiClientV2
        .get<SupplierInquiryDetail>(`${BASE}/${inquiryId}`)
        .then((r) => toDraftItem(r.data)),
    enabled: !!inquiryId,
  })
}

// ─── Autosave (PATCH /{id}) ─────────────────────────────────────────────────────

export interface InquiryDraftPatch {
  emailSubject?: string
  emailBody?: string
  reminderEmailSubject?: string
  reminderEmailBody?: string
  recipientsTo?: string[]
  recipientsCc?: string[]
  frameworks?: string[]
  /** Draft only: the regulations it asks, as keys; makes the pick explicit. */
  regulationKeys?: string[]
  /** Draft only: drop the pick and follow Settings › Regelwerke again. */
  followOrgRegulations?: boolean
  articleIds?: string[]
  language?: string
  attachmentFormat?: AttachmentFormat
  dueAt?: string
}

/** PATCH one inquiry. Shared with the agent chat, so the request cannot drift. */
export async function patchInquiryRequest(
  id: string,
  changes: InquiryDraftPatch,
): Promise<InquiryDraftItem> {
  const { data } = await apiClientV2.patch<SupplierInquiryDetail>(
    `${BASE}/${id}`,
    changes,
  )
  return toDraftItem(data)
}

/**
 * Put a saved inquiry back into the caches every inquiry surface reads. Shared
 * with the chat, and load-bearing there: an already-mounted panel does NOT refetch
 * on its own, so skipping this reads as the write having failed.
 */
export function syncPatchedInquiryCaches(
  qc: QueryClient,
  id: string,
  data: InquiryDraftItem,
): void {
  qc.invalidateQueries({ queryKey: ["mc", "inquiries", "detail", id] })
  qc.invalidateQueries({ queryKey: ["mc", "inquiries", "full-detail", id] })
  qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
  // Keep the shared draft-load cache in sync with the saved item. Without this
  // the cache keeps the recipients/copy captured at create time; a later visit
  // (e.g. opening this inquiry to remind) serves that stale snapshot, and the
  // page's one-time seeding effect latches empty recipients until a hard refresh.
  qc.setQueriesData<{ reference: string; inquiries: InquiryDraftItem[] }>(
    { queryKey: ["mc", "inquiries", "draft", "load"] },
    (old) =>
      old
        ? {
            ...old,
            inquiries: old.inquiries.map((i) => (i.id === id ? data : i)),
          }
        : old,
  )
}

export function usePatchInquiry() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: InquiryDraftPatch }) =>
      patchInquiryRequest(id, changes),
    onSuccess: (data, { id }) => syncPatchedInquiryCaches(qc, id, data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

// ─── Send (POST /{id}/send) ─────────────────────────────────────────────────────
// One call sends ONE inquiry (its own email + thread). The composer fans out a bulk
// draft into N of these — one per inquiry id — so each supplier gets its own thread.
export interface InquirySendIn {
  language?: Lang
  attachmentFormat?: AttachmentFormat
  emailSubject?: string
  emailBody?: string
  emailTo?: string
  // Full lists sent alongside emailTo so a send that beats the autosave still uses them.
  recipientsTo?: string[]
  recipientsCc?: string[]
  dueAt?: string
  // For manual reminders (isReminder=true), reminderBody is used as the email body;
  // reminderSubject is stored/displayed in the composer but the outbound subject is
  // forced to "Re: {original subject}" for correct mail-client threading.
  isReminder?: boolean
  reminderSubject?: string
  reminderBody?: string
  // MC_NOTES: ids of the supplier's warning notes the user confirmed. Without them a
  // send to a supplier that has such notes is answered 409 SUPPLIER_WARNING and
  // nothing goes out (see isSupplierWarningError). Sent as acknowledged_note_ids.
  acknowledgedNoteIds?: string[]
  // Ids of other suppliers' inquiries to the same To address the user confirmed.
  // Without them such a send is answered 409 RECIPIENT_ALREADY_INQUIRED and nothing
  // goes out (see isRecipientOverlapError). Sent as acknowledged_inquiry_ids.
  acknowledgedInquiryIds?: string[]
  // The user confirmed sending although the signature (or the PDF footer) still holds
  // placeholder text. Without it such a send is answered 409 SIGNATURE_PLACEHOLDER.
  acknowledgePlaceholderSignature?: boolean
}

// ─── Placeholder signature (409 SIGNATURE_PLACEHOLDER on the send) ───────────────

/** The sign-off lines still holding placeholder text when a send was refused for
 *  them, by the setting that holds them, or null for any other error. Nothing was sent.
 *  The error body skips the success interceptor, so its keys arrive snake_case. */
export function signoffPlaceholderLines(
  error: unknown,
): { signature: string[]; footer: string[] } | null {
  const detail = getApiErrorDetail(error)
  if (detail?.code !== "SIGNATURE_PLACEHOLDER") return null
  const lines = (value: unknown) =>
    Array.isArray(value) ? (value as string[]) : []
  return {
    signature: lines(detail.signature_lines),
    footer: lines(detail.footer_lines),
  }
}

// ─── Supplier warning (409 SUPPLIER_WARNING on the send) ─────────────────────────

/**
 * The detail of a refusal for a supplier warning: the 409 of any send path, which is
 * `{ detail: { code, message, notes } }`. A QUESTIONS_SEND_FAILED that carries the
 * real error under `underlying` is read the same way, as a harmless fallback. Null
 * for any other error.
 */
function supplierWarningDetail(error: unknown): Record<string, unknown> | null {
  const detail = getApiErrorDetail(error)
  if (detail?.code === "SUPPLIER_WARNING") return detail
  const underlying = detail?.underlying
  if (
    typeof underlying === "object" &&
    underlying !== null &&
    (underlying as { code?: unknown }).code === "SUPPLIER_WARNING"
  ) {
    return underlying as Record<string, unknown>
  }
  return null
}

/** Whether a failed send was refused because the supplier has warning notes the
 *  caller has not acknowledged. Nothing was sent and nothing changed. */
export function isSupplierWarningError(error: unknown): boolean {
  return supplierWarningDetail(error) !== null
}

/**
 * The warning notes behind that refusal as NoteOut, or [] when the body carries none.
 * They are ALL the supplier's active warning notes, so a retry acknowledges every id.
 * An error body skips the success interceptor, so it arrives as the backend wrote
 * it (snake_case keys, ISO date strings) and is converted here the same way.
 */
export function supplierWarningNotes(error: unknown): NoteOut[] {
  const notes = supplierWarningDetail(error)?.notes
  return Array.isArray(notes)
    ? convertDateStringsToDates(convertKeysToCamelCase(notes as NoteOut[]))
    : []
}

// ─── Recipient overlap (409 RECIPIENT_ALREADY_INQUIRED on the send) ──────────────

/** Another supplier's inquiry that goes to one of this send's To addresses: a draft,
 *  an open one, or one answered or closed in the last 30 days (TUR-1812). */
export interface RecipientOverlap {
  inquiryId: string
  supplierName: string | null
  reference: string | null
  status: string
  sentAt: Date | null
  address: string
}

function recipientOverlapDetail(
  error: unknown,
): Record<string, unknown> | null {
  const detail = getApiErrorDetail(error)
  return detail?.code === "RECIPIENT_ALREADY_INQUIRED" ? detail : null
}

/** Whether a failed send was refused because another supplier's inquiry goes to the
 *  same address and the caller has not acknowledged it. Nothing was sent. */
export function isRecipientOverlapError(error: unknown): boolean {
  return recipientOverlapDetail(error) !== null
}

/** The other inquiries behind that refusal, or [] when the body carries none. They
 *  are ALL the current overlaps, so a retry acknowledges every id. The error body
 *  skips the success interceptor, so its keys and dates are converted here. */
export function recipientOverlaps(error: unknown): RecipientOverlap[] {
  const conflicts = recipientOverlapDetail(error)?.conflicts
  if (!Array.isArray(conflicts)) return []
  // `sentAt` is not one of the keys convertDateStringsToDates knows, so it is
  // revived here rather than by widening that app-wide list.
  return convertKeysToCamelCase(
    conflicts as (Omit<RecipientOverlap, "sentAt"> & {
      sentAt: string | null
    })[],
  ).map((other) => ({
    ...other,
    sentAt: other.sentAt ? new Date(other.sentAt) : null,
  }))
}

// The send endpoint expects the stage flag spelled "first-request" / "reminder".
const STAGE_WIRE: Record<Stage, "first-request" | "reminder"> = {
  first: "first-request",
  reminder: "reminder",
}

export function useSendInquiry() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      stage = "first",
      overrides,
    }: {
      id: string
      stage?: Stage
      overrides?: InquirySendIn
    }) =>
      apiClientV2
        .post<SupplierInquiryDetail>(`${BASE}/${id}/send`, {
          stage: STAGE_WIRE[stage],
          ...(overrides ?? {}),
        })
        .then((r) => toDraftItem(r.data)),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "detail", id] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
      // A sent inquiry moves the "Datenlücken" tile's open-clarifications
      // bucket (TUR-1539), same key invalidateMcEntityCaches touches on a
      // master-data write.
      qc.invalidateQueries({ queryKey: ["mc", "master-data", "data-gaps"] })
    },
  })
  // Reminders take a dedicated key on the backend; the send endpoint above
  // covers both, so we gate on MC_INQUIRIES_SEND (the strict superset that
  // includes reminders in practice — a member who can send can remind).
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

// Direct send — used for bulk fan-out where firing N concurrent mutateAsync calls
// on the same mutation instance causes React Query to serialize/interfere with them.
// Each inquiry gets its own independent fetch; callers can invalidate caches if needed.
export async function sendInquiryDirect(
  id: string,
  overrides?: InquirySendIn,
): Promise<void> {
  await apiClientV2.post<SupplierInquiryDetail>(
    `${BASE}/${id}/send`,
    overrides ?? {},
  )
}

/** POST /{id}/send-test — mails the first request to the CALLER only.
 *
 *  Takes an id and nothing else: no recipient to pass means no recipient to get
 *  wrong. The Abfrage stays a draft and is assigned no reference, so a reply to
 *  this mail cannot thread back to it. */
export async function sendInquiryTest(id: string): Promise<{ sentTo: string }> {
  const { data } = await apiClientV2.post<{ sentTo: string }>(
    `${BASE}/${id}/send-test`,
  )
  return data
}

// Pull the filename out of a Content-Disposition header, preferring the RFC 5987
// `filename*=UTF-8''…` form (percent-decoded) over the plain quoted `filename=`.
// Returns undefined when the header is absent or carries no filename.
export function parseContentDispositionFilename(
  header: string | undefined,
): string | undefined {
  if (!header) return undefined
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""))
    } catch {
      // fall through to the plain form on a malformed percent-encoding
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim() || undefined
}

// ─── Download questionnaire (GET /{id}/questionnaire.{pdf,xlsx}) ─────────────────
// Fetches the backend-rendered attachment as a blob and triggers a browser download.
// Used by the preview's Download button (also the "send manually" fallback). `format`
// picks the endpoint/extension — both formats are always available regardless of the
// inquiry's chosen send format.
export async function downloadQuestionnaire(
  inquiryId: string,
  opts: { stage?: Stage; lang?: Lang; format?: AttachmentFormat } = {},
): Promise<void> {
  const ext = opts.format === "excel" ? "xlsx" : "pdf"
  const res = await apiClientV2.get<Blob>(
    `${BASE}/${inquiryId}/questionnaire.${ext}`,
    {
      params: { stage: opts.stage ?? "first", lang: opts.lang },
      responseType: "blob",
    },
  )
  // Honor the backend's reference-based filename (Content-Disposition) so the download
  // matches the emailed attachment; fall back to the id-based name if the header is
  // missing (e.g. not exposed by CORS in some environments).
  const url = URL.createObjectURL(res.data)
  const a = document.createElement("a")
  a.href = url
  a.download =
    parseContentDispositionFilename(res.headers?.["content-disposition"]) ??
    `questionnaire-${inquiryId}.${ext}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ─── Questionnaire Excel preview (GET /{id}/questionnaire.xlsx as a blob) ─────────
// Fetches the backend-rendered workbook for the composer's live Excel preview. Cached
// per (id, stage, lang, articleSig, scopeSig, dueAt) so it refetches only when the persisted
// article scope, regulation pick, deadline, or stage/lang changes; keepPreviousData keeps the current workbook on
// screen while a new one renders. Returns the raw Blob — the component owns the object-URL
// lifecycle. Unlike the HTML preview, the workbook reflects the SAVED state, so it refreshes
// after an autosave rather than on every keystroke.
export function useQuestionnaireXlsx(
  inquiryId: string | null,
  opts: {
    stage?: Stage
    lang?: Lang
    articleSig?: string
    /** The persisted regulation pick: the workbook drops unpicked blocks. */
    scopeSig?: string
    dueAt?: string | null
    enabled?: boolean
  } = {},
) {
  const {
    stage = "first",
    lang,
    articleSig,
    scopeSig,
    dueAt,
    enabled = true,
  } = opts
  return useQuery({
    queryKey: [
      "mc",
      "inquiries",
      "questionnaire-xlsx",
      inquiryId,
      stage,
      lang,
      articleSig,
      scopeSig,
      dueAt,
    ],
    queryFn: async () => {
      const res = await apiClientV2.get<Blob>(
        `${BASE}/${inquiryId}/questionnaire.xlsx`,
        { params: { stage, lang }, responseType: "blob" },
      )
      return res.data
    },
    enabled: !!inquiryId && enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })
}
